import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import Button, { IButton } from "../Button/Button";
import clsx from "clsx";
import {
  MdDelete,
  MdDeleteOutline,
  MdGridView,
  MdOutlineAttachment,
  MdOutlineImage,
  MdPictureAsPdf,
  MdTableView,
} from "react-icons/md";
import { motion } from "framer-motion";
import { ACCEPTED_ATTACHMENT_TYPES, ICON_SIZES } from "../../helpers/constants";
import { useTranslation } from "react-i18next";
import useCollapsibles from "../../hooks/useCollapsibles";
import useMobile from "../../hooks/useMobile";
import CommonTable from "../Tables/CommonTable";
import { formatFileSize, getFileType } from "src/helpers/utils";
import usePinnedColumns from "src/hooks/usePinnedColumns";
import useCurrentUser from "src/hooks/useCurrentUser";
import useLocalStorageState from "use-local-storage-state";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { EButtonKind } from "../Button/ButtonTypes";
import { EAttachmentsTableKeys } from "../Tables/tableIds";

export interface IFilePreview {
  file: File;
  name: string;
  size: number;
  formattedSize: string;
  type: string;
  preview: string;
  version: string;
}

export enum EAttachmentsTableViewStyle {
  TABLE = "table",
  GRID = "grid",
}

export interface IAttachmentsSelector {
  actions?: IButton[];
  onChange: (data: any) => void;
  savedAttachments: Array<any>;
  onClick: (data: any) => void;
}

const ATTACHMENTS_KEY = "attachments-table";
const DEFAULTS: {
  tableStyle: EAttachmentsTableViewStyle;
} = {
  tableStyle: EAttachmentsTableViewStyle.GRID,
};

const AttachmentsSelector: React.FC<IAttachmentsSelector> = ({
  actions = [],
  onChange,
  savedAttachments = [],
  onClick,
}) => {
  const [unsavedAttachments, setUnsavedAttachments] = useState<any[]>([]);
  const [deletedExistingAttachments, setDeletedExistingAttachments] = useState<
    any[]
  >([]);
  const { userInfo } = useCurrentUser();
  const [attachmentsTableStyle, setAttachmentsTableStyle] =
    useLocalStorageState(`${userInfo?._id}-${ATTACHMENTS_KEY}`, {
      defaultValue: DEFAULTS,
    });

  const unifiedAttachmentsList = useMemo(() => {
    return [
      ...(savedAttachments?.map?.((theFile: any) => {
        return {
          name: theFile.originalName,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() || "Desconocido",
          isSaved: true,
        };
      }) || []),
      ...(unsavedAttachments?.map?.((theFile: any) => {
        return {
          name: theFile.name,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() || "Desconocido",
          isSaved: false,
        };
      }) || []),
    ];
  }, [savedAttachments, unsavedAttachments]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const {
    collapsiblePreferences,
    updateCollapsible,
    toggleTransactionPageMobileCollapsible,
    isTransactionPageMobileCollapsibleOpen,
  } = useCollapsibles();
  const { isMobile } = useMobile();
  const [dragging, setDragging] = useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isPinnedColumn, updatePinnedColumns } = usePinnedColumns();

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    onChange({
      saved: savedAttachments,
      deleted: deletedExistingAttachments,
      unsaved: unsavedAttachments,
    });
  }, [unsavedAttachments, deletedExistingAttachments]);

  const totalFiles = useMemo(() => {
    return [...savedAttachments, ...unsavedAttachments].length;
  }, [savedAttachments, unsavedAttachments]);

  const handleFileChange = useCallback((files: FileList | null) => {
    if (!files) return;

    const selectedFiles: IFilePreview[] = Array.from(files).map((file) => ({
      file,
      name: file.name,
      size: file.size,
      formattedSize: formatFileSize(file.size),
      type: file.type,
      formattedType: getFileType(file),
      preview: URL.createObjectURL(file),
      version: "v3",
    }));

    setUnsavedAttachments((prevFiles) => {
      const newFiles = selectedFiles.filter(
        (newFile) =>
          !prevFiles.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
              existingFile.size === newFile.size
          )
      );

      return [...prevFiles, ...newFiles];
    });
  }, []);

  const deleteRow = (data: any) => {
    const fileToDelete = `${data?.name}${data?.size}`;

    if (data?.isSaved) {
      const deletedAttachments = savedAttachments?.filter?.((saved: any) => {
        return fileToDelete === `${saved.originalName}${saved.size}`;
      });
      setDeletedExistingAttachments(deletedAttachments);
    } else {
      const newUnsaved = unsavedAttachments?.filter?.((unsaved: any) => {
        return fileToDelete !== `${unsaved.name}${unsaved.size}`;
      });
      setUnsavedAttachments(newUnsaved);
    }
  };

  const deleteCard = (data: any) => {
    const fileToDelete = `${data?.name}${data?.size}`;

    if (data?.isSaved) {
      const deletedAttachments = savedAttachments?.filter?.((saved: any) => {
        return fileToDelete === `${saved.originalName}${saved.size}`;
      });
      setDeletedExistingAttachments(deletedAttachments);
    } else {
      const newUnsaved = unsavedAttachments?.filter?.((unsaved: any) => {
        return fileToDelete !== `${unsaved.name}${unsaved.size}`;
      });
      setUnsavedAttachments(newUnsaved);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files) {
      setIsLoading(true);
      await handleFileChange(event.dataTransfer.files);
      setIsLoading(false);
    }
  };

  const showAttachmentsTable = useMemo(() => {
    const hasFiles = totalFiles > 0;

    return hasFiles;
  }, [totalFiles]);

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "NOMBRE",
        flex: 1,
        minWidth: 200,
        pinned: isPinnedColumn("transactionAttachmentsNamePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionAttachmentsNamePinned: isPinned,
              });
            },
          },
        },
      },
      {
        field: "formattedSize",
        headerName: "TAMAÑO",
        pinned: isPinnedColumn("transactionAttachmentsSizePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionAttachmentsSizePinned: isPinned,
              });
            },
          },
        },
      },
      {
        field: "typeFormatted",
        headerName: "TIPO",
        pinned: isPinnedColumn("transactionAttachmentsTypePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionAttachmentsTypePinned: isPinned,
              });
            },
          },
        },
      },
      {
        cellRenderer: "CellWithButtonsRenderer",
        headerName: "ACCIONES",
        pinned: isPinnedColumn("transactionAttachmentsActionsPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionAttachmentsActionsPinned: isPinned,
              });
            },
          },
        },
        sortable: false,
        cellRendererParams: (params: any) => {
          return {
            buttons: [
              {
                icon:
                  params?.data?.type !== "application/pdf" ? (
                    <MdOutlineImage size={ICON_SIZES.SM} />
                  ) : (
                    <MdPictureAsPdf size={ICON_SIZES.SM} />
                  ),
                onClick: (e: any) => {
                  e?.stopPropagation?.();
                  const { data } = params;

                  onClick({
                    saved: data?.isSaved,
                    index: params?.node?.rowIndex,
                  });
                },
                className: clsx(["grid-button"]),
              },
              {
                icon: <MdDeleteOutline size={ICON_SIZES.SM} />,
                onClick: () => {
                  const { data } = params;
                  deleteRow(data);
                },
                className: clsx(["grid-button"]),
                kind: EButtonKind.DANGER,
                hide: false,
              },
            ],
          };
        },
      },
    ];
  }, [isPinnedColumn]);

  return (
    <div
      className={clsx(["flex", "w-full", "gap-4", "flex-col", "relative"])}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={clsx(["flex", "w-full", "flex-col", "gap-4"])}>
        {showAttachmentsTable && (
          <>
            {isMobile ? (
              <>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-1",
                    "md:gap-2",
                  ])}
                >
                  {unifiedAttachmentsList.map((attachment, index) => {
                    return (
                      <motion.div
                        className={clsx([
                          "flex",
                          "w-full",
                          "flex-row",
                          "gap-2",
                          "rounded-md",
                          "p-2",
                          "shadow-md",
                          "cursor-pointer",
                          "dark:bg-gray-900",
                        ])}
                        key={index}
                        onClick={() => {
                          onClick({
                            saved: attachment?.isSaved,
                            index,
                          });
                        }}
                        initial={{
                          opacity: 0,
                          x: index % 2 === 0 ? -100 : 100,
                        }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.2,
                          ease: "easeOut",
                          delay: index * 0.1,
                        }}
                      >
                        <div
                          className={clsx([
                            "text-cyan-400",
                            "dark:text-cyan-400",
                          ])}
                        >
                          {attachment?.type === "application/pdf" ? (
                            <MdPictureAsPdf size={ICON_SIZES.LG} />
                          ) : (
                            <MdOutlineImage size={ICON_SIZES.LG} />
                          )}
                        </div>
                        <div
                          className={clsx([
                            "flex",
                            "w-full",
                            "justify-center",
                            "flex-col",
                            "truncate",
                          ])}
                        >
                          <div className={clsx(["text-sm", "truncate"])}>
                            {attachment?.name}
                          </div>
                          <div
                            className={clsx([
                              "flex",
                              "flex-row",
                              "gap-2",
                              "w-full",
                              "text-sm",
                            ])}
                          >
                            {/* <div>{attachment?.typeFormatted}</div> */}
                            <div>{attachment?.formattedSize}</div>
                          </div>
                        </div>
                        <div>
                          <Button
                            kind={EButtonKind.DANGER}
                            tooltip={t("attachments_selector.buttons.delete")}
                            icon={<MdDelete size={ICON_SIZES.MD} />}
                            onClick={(e) => {
                              e?.stopPropagation?.();
                              deleteCard(attachment);
                            }}
                          />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                {attachmentsTableStyle.tableStyle ===
                  EAttachmentsTableViewStyle.TABLE && (
                  <CommonTable
                    onSelectionChange={(data: any) => {}}
                    columns={columns}
                    data={unifiedAttachmentsList}
                    singleSelection
                    settingsKey={EAttachmentsTableKeys.ATTACHMENTS_SELECTOR}
                  />
                )}
                {attachmentsTableStyle.tableStyle ===
                  EAttachmentsTableViewStyle.GRID && (
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-2",
                      "gap-1",
                      "md:gap-2",
                    ])}
                  >
                    {unifiedAttachmentsList.map((attachment, index) => {
                      return (
                        <motion.div
                          className={clsx([
                            "flex",
                            "w-full",
                            "flex-row",
                            "gap-2",
                            "rounded-md",
                            "p-2",
                            "shadow-sm",
                            "dark:shadow-gray-900",
                            "cursor-pointer",
                            "dark:bg-gray-900",
                            "border",
                            "border-blue-100",
                            "dark:border-purple-950",
                          ])}
                          key={index}
                          onClick={() => {
                            onClick({
                              saved: attachment?.isSaved,
                              index,
                            });
                          }}
                          initial={{
                            opacity: 0,
                            x: index % 2 === 0 ? -100 : 100,
                          }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.2,
                            ease: "easeOut",
                            delay: index * 0.1,
                          }}
                        >
                          <div
                            className={clsx([
                              "text-cyan-400",
                              "dark:text-cyan-400",
                            ])}
                          >
                            {attachment?.type === "application/pdf" ? (
                              <MdPictureAsPdf size={ICON_SIZES.LG} />
                            ) : (
                              <MdOutlineImage size={ICON_SIZES.LG} />
                            )}
                          </div>
                          <div
                            className={clsx([
                              "flex",
                              "w-full",
                              "justify-center",
                              "flex-col",
                              "truncate",
                            ])}
                          >
                            <div className={clsx(["text-sm", "truncate"])}>
                              {attachment?.name}
                            </div>
                            <div
                              className={clsx([
                                "flex",
                                "flex-row",
                                "gap-2",
                                "w-full",
                                "text-sm",
                              ])}
                            >
                              {/* <div>{attachment?.typeFormatted}</div> */}
                              <div>{attachment?.formattedSize}</div>
                            </div>
                          </div>
                          <div>
                            <Button
                              kind={EButtonKind.DANGER}
                              tooltip={t("attachments_selector.buttons.delete")}
                              icon={<MdDelete size={ICON_SIZES.MD} />}
                              onClick={(e) => {
                                e?.stopPropagation?.();
                                deleteCard(attachment);
                              }}
                            />
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}
        <div
          className={clsx([
            "flex",
            "w-full",
            "flex-col",
            "md:flex-row",
            "justify-center",
            "items-center",
            "md:items-center",
            "gap-4",
            "md:gap-6",
            "border-2",
            "border-dashed",
            "p-4",
            "md:p-4",
            // "cursor-pointer",
            "hover:border-red-500 hover:dark:border-purple-400/50",
            dragging
              ? "border-red-500 dark:border-gray-400"
              : "border-gray-200 dark:border-gray-600",
          ])}
          // onClick={() => openFileDialog()}
        >
          <div
            className={clsx([
              "flex",
              "w-full",
              "items-center",
              "md:items-start",
              "justify-center",
              "md:text-left",
              "flex-col",
            ])}
          >
            <div
              className={clsx([
                "text-sm",
                "font-medium",
                "md:text-left",
                "text-center",
              ])}
            >
              {totalFiles === 0 ? (
                <>{t("attachments_selector.no_attachments")}</>
              ) : (
                <>{t("attachments_selector.attach_more")}</>
              )}
            </div>
            <p
              className={clsx([
                "text-sm",
                "md:text-sm",
                "text-center",
                "md:text-left",
              ])}
            >
              {t("attachments_selector.message")}
            </p>
          </div>
          <div className={clsx(["flex", "flex-row", "gap-2"])}>
            <ButtonGroup
              buttons={[
                {
                  text: t("attachments_selector.buttons.select"),
                  icon: <MdOutlineAttachment size={ICON_SIZES.MD} />,
                  isLoading,
                  disabled: isLoading,
                  onClick: (e: any) => {
                    e?.stopPropagation?.();
                    openFileDialog();
                  },
                },
                {
                  tooltip: t("attachments_selector.buttons.table"),
                  hide: !totalFiles || isMobile,
                  icon: (
                    <MdTableView
                      size={ICON_SIZES.MD}
                      className={clsx([
                        {
                          "text-red-500 dark:text-purple-500":
                            attachmentsTableStyle.tableStyle ===
                            EAttachmentsTableViewStyle.TABLE,
                        },
                      ])}
                    />
                  ),
                  onClick: () =>
                    setAttachmentsTableStyle({
                      tableStyle: EAttachmentsTableViewStyle.TABLE,
                    }),
                },
                {
                  tooltip: t("attachments_selector.buttons.grid"),
                  hide: !totalFiles || isMobile,
                  icon: (
                    <MdGridView
                      size={ICON_SIZES.MD}
                      className={clsx([
                        {
                          "text-red-500 dark:text-purple-500":
                            attachmentsTableStyle.tableStyle ===
                            EAttachmentsTableViewStyle.GRID,
                        },
                      ])}
                    />
                  ),
                  onClick: () =>
                    setAttachmentsTableStyle({
                      tableStyle: EAttachmentsTableViewStyle.GRID,
                    }),
                },
              ]}
              align="center"
            />
          </div>
          <input
            type="file"
            multiple
            accept={ACCEPTED_ATTACHMENT_TYPES}
            ref={fileInputRef}
            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
              setIsLoading(true);

              await handleFileChange(e?.target?.files);
              setIsLoading(false);
            }}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(AttachmentsSelector);
